import debounce from 'lodash/debounce';
import { useCallback } from 'react';
import type { UsePaginationProps } from 'react-instantsearch-core';
import { usePagination } from 'react-instantsearch-core';
import { useIntl } from 'react-intl';

import { getLinkAriaLabel, getPathnameFromAsPath } from '@/utils/urlUtils';

import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { useRouter } from '@/hooks/useRouter';
import { messages } from '@/messages/boutique';
import { useRegionProvider } from '@/providers/RegionProvider';

import { getPaginationUrl } from './utils';

import * as S from './HitsPagination.styles';

export const HitsPagination = (props: UsePaginationProps) => {
  const { currentRefinement, isFirstPage, isLastPage, nbPages, pages, refine } =
    usePagination(props);
  const { formatMessage } = useIntl();
  const { asPath, locale, push, query } = useRouter();
  const {
    locale: { code: languageCode },
  } = useRegionProvider();
  const pathname = getPathnameFromAsPath(asPath);

  const queryNbPages = parseInt(query.page as string);

  const handleClick = useCallback(
    (page: number) => {
      scrollTo({ top: 0 });
      window.parentIFrame?.scrollToOffset(0, 0);
      refine(page);
    },
    [refine, push, pathname, locale],
  );

  // Needed because it's not possible to mock the debounce function in tests
  const debouncedHandleClick =
    process.env.NODE_ENV === 'test' ? handleClick : debounce(handleClick, 300);

  if (nbPages <= 1 || nbPages < queryNbPages || queryNbPages <= 0) {
    return null;
  }

  return (
    <>
      <S.Wrapper
        data-test-id='HitsPaginationWrapper'
        gridGap={2}
        justifyItems='center'
        padding={2}
      >
        <Flex alignItems='center' gridGap={2}>
          {!isFirstPage && (
            <S.ChevronLink
              $isDisabled={isFirstPage}
              aria-disabled={isFirstPage}
              aria-label={
                getLinkAriaLabel(
                  getPaginationUrl(
                    pathname as string,
                    currentRefinement,
                    locale,
                  ),
                  languageCode,
                ) ||
                formatMessage(messages.goToPage, {
                  page: currentRefinement,
                })
              }
              data-test-id='HitsPaginationChevronLink-FirstPage'
              href={getPaginationUrl(
                pathname as string,
                currentRefinement,
                locale,
              )}
              onClick={(evt) => {
                if (isFirstPage) {
                  return;
                }
                evt.preventDefault();
                debouncedHandleClick(currentRefinement - 1);
              }}
            >
              <Icon
                color={isFirstPage ? 'disableGrey' : 'secondary'}
                name='chevronLeft'
                variant='bold'
              />
            </S.ChevronLink>
          )}

          {pages.map((page) => {
            const pageNumber = page + 1;
            const ariaLabel =
              page === 0
                ? getLinkAriaLabel(
                    getPaginationUrl(pathname as string, 1, locale),
                    languageCode,
                  )
                : undefined;

            return (
              <S.PageLink
                key={page}
                $isActive={currentRefinement === page}
                aria-label={
                  ariaLabel ||
                  formatMessage(messages.goToPage, { page: pageNumber })
                }
                data-test-id={`HitsPaginationPageLink-${pageNumber}`}
                href={getPaginationUrl(pathname as string, pageNumber, locale)}
                onClick={(evt) => {
                  evt.preventDefault();
                  debouncedHandleClick(page);
                }}
              >
                {pageNumber}
              </S.PageLink>
            );
          })}

          {!isLastPage && (
            <S.ChevronLink
              $isDisabled={isLastPage}
              aria-disabled={isLastPage}
              aria-label={formatMessage(messages.goToPage, {
                page: currentRefinement + 2,
              })}
              data-test-id='HitsPaginationChevronLink-NextPage'
              href={getPaginationUrl(
                pathname as string,
                currentRefinement + 2,
                locale,
              )}
              onClick={(evt) => {
                if (isLastPage) {
                  return;
                }
                evt.preventDefault();
                debouncedHandleClick(currentRefinement + 1);
              }}
            >
              <Icon
                color={isLastPage ? 'disableGrey' : 'secondary'}
                name='chevronRight'
                variant='bold'
              />
            </S.ChevronLink>
          )}
        </Flex>

        {!isLastPage && (
          <S.PaginationCTA
            data-test-id='HitsPaginationLegacyButton-NextPage'
            disabled={isLastPage}
            label={formatMessage(messages.nextPage)}
            onClick={(evt) => {
              evt.preventDefault();
              debouncedHandleClick(currentRefinement + 1);
            }}
            role='link'
          />
        )}
      </S.Wrapper>
    </>
  );
};

HitsPagination.displayName = 'HitsPagination';
